/**
 * SquawkV2WebSocket - WebSocket + MediaSource Extensions audio streaming client.
 *
 * Ported from audio-v2 backend client (client/src/listener/services/squawk_web_socket.ts).
 * Connects to the v2 audio backend via WebSocket, receives binary audio chunks
 * (WebM/Opus), and plays them through MediaSource Extensions with buffer management.
 */
import * as Sentry from "@sentry/browser";
import type {
  ServerMessage,
  StreamStartMessage,
  StreamEndMessage,
  ClipMessage,
  SubscribedMessage,
  ErrorServerMessage,
  SubscribeCommand,
  UnsubscribeCommand,
  PingCommand,
  ConnectionStatus,
} from "./types";
import {
  buildPlayerFailureTags,
  getPlayerFailureDiagnostics,
} from "../playerNetworkDiagnostics";

const MAX_RECONNECT_ATTEMPTS = 10;

type EventHandler = (...args: unknown[]) => void;

export class SquawkV2WebSocket {
  private ws: WebSocket | null = null;
  private url: string;
  private active_audio_element: HTMLAudioElement | null = null;
  private media_source: MediaSource | null = null;
  private source_buffer: SourceBuffer | null = null;
  private current_volume = 1.0;
  private chunk_queue: ArrayBuffer[] = [];
  private is_streaming = false;
  private current_object_url: string | null = null;
  private status: ConnectionStatus = "disconnected";
  private reconnect_attempts = 0;
  private max_reconnect_delay = 30000;
  private reconnect_timeout: ReturnType<typeof setTimeout> | null = null;
  private active_session_id: string | null = null;
  private pending_clip_meta: ClipMessage | null = null;
  private intentional_disconnect = false;
  private max_buffer_ms: number;
  private media_error_count = 0;
  private periodic_log_interval: ReturnType<typeof setInterval> | null = null;
  private last_known_current_time = 0;
  private last_current_time_update = 0;
  private gap_recovery_attempts = 0;
  private max_gap_recovery_attempts = 3;
  // Session-scoped override: once a codec_mismatch fires, future reconnects
  // ignore the server's announced content_type and use audio/webm;codecs=opus
  // (Sentry data shows opus is the actual stream codec in 100% of mismatch
  // cases — see Sentry Issue NEWSQUAWK-PORTALS-13K).
  private force_opus_codec = false;

  // Simple event emitter
  private listeners: Map<string, Set<EventHandler>> = new Map();

  // Bound WebSocket handlers for cleanup
  private boundWsOpen: (() => void) | null = null;
  private boundWsMessage: ((event: MessageEvent) => void) | null = null;
  private boundWsClose: ((event: CloseEvent) => void) | null = null;
  private boundWsError: ((event: Event) => void) | null = null;

  constructor(url: string, max_buffer_ms = 1000) {
    this.url = url;
    this.max_buffer_ms = max_buffer_ms;
  }

  // -- Event emitter --

  on(event: string, handler: EventHandler): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);
  }

  off(event: string, handler: EventHandler): void {
    this.listeners.get(event)?.delete(handler);
  }

  private emit(event: string, ...args: unknown[]): void {
    this.listeners.get(event)?.forEach((handler) => {
      try {
        handler(...args);
      } catch (err) {
        console.error(`[SquawkV2] Event handler error (${event}):`, err);
      }
    });
  }

  // -- Public API --

  connect(): void {
    if (this.ws) return;

    this.intentional_disconnect = false;
    this.set_status("connecting");

    try {
      this.ws = new WebSocket(this.url);
      this.ws.binaryType = "arraybuffer";

      this.boundWsOpen = () => this.handle_open();
      this.boundWsMessage = (event) => this.handle_message(event);
      this.boundWsClose = (event) => this.handle_close(event);
      this.boundWsError = () => this.handle_error();

      this.ws.addEventListener("open", this.boundWsOpen);
      this.ws.addEventListener("message", this.boundWsMessage);
      this.ws.addEventListener("close", this.boundWsClose);
      this.ws.addEventListener("error", this.boundWsError);
    } catch (error) {
      console.error("[SquawkV2] Failed to create WebSocket:", error);
      this.schedule_reconnect();
    }
  }

  disconnect(): void {
    this.intentional_disconnect = true;
    this.stop_periodic_logging();

    if (this.reconnect_timeout !== null) {
      clearTimeout(this.reconnect_timeout);
      this.reconnect_timeout = null;
    }

    if (this.ws) {
      // Remove listeners before closing to prevent accumulation
      if (this.boundWsOpen) this.ws.removeEventListener("open", this.boundWsOpen);
      if (this.boundWsMessage) this.ws.removeEventListener("message", this.boundWsMessage);
      if (this.boundWsClose) this.ws.removeEventListener("close", this.boundWsClose);
      if (this.boundWsError) this.ws.removeEventListener("error", this.boundWsError);
      this.boundWsOpen = null;
      this.boundWsMessage = null;
      this.boundWsClose = null;
      this.boundWsError = null;
      this.ws.close();
      this.ws = null;
    }

    this.stop_stream();
    this.set_status("disconnected");
  }

  subscribe(keywords: string[]): void {
    const msg: SubscribeCommand = { type: "subscribe", keywords };
    this.send(msg);
  }

  unsubscribe(keywords: string[]): void {
    const msg: UnsubscribeCommand = { type: "unsubscribe", keywords };
    this.send(msg);
  }

  ping(): void {
    const msg: PingCommand = { type: "ping" };
    this.send(msg);
  }

  get_status(): ConnectionStatus {
    return this.status;
  }

  set_volume(level: number): void {
    this.current_volume = Math.max(0, Math.min(1, level));
    if (this.active_audio_element) {
      this.active_audio_element.volume = this.current_volume;
    }
  }

  destroy(): void {
    this.disconnect();
    this.listeners.clear();
  }

  // -- Logging --

  private log(message: string, ...args: unknown[]): void {
    console.log(`[SquawkV2] ${message}`, ...args);
  }

  private log_error(message: string, ...args: unknown[]): void {
    console.error(`[SquawkV2] ERROR: ${message}`, ...args);
  }

  private log_warn(message: string, ...args: unknown[]): void {
    console.warn(`[SquawkV2] WARN: ${message}`, ...args);
  }

  // -- Buffer helpers --

  private is_quota_exceeded_error(error: unknown): boolean {
    return error instanceof DOMException && error.name === "QuotaExceededError";
  }

  private is_buffer_ready(): boolean {
    return (
      this.source_buffer !== null &&
      this.media_source !== null &&
      this.media_source.readyState === "open"
    );
  }

  private has_media_error(): boolean {
    return this.active_audio_element?.error !== null && this.active_audio_element?.error !== undefined;
  }

  // -- WebSocket handlers --

  private handle_open(): void {
    this.log("WebSocket connected");
    this.reconnect_attempts = 0;
    this.set_status("connected");
    this.emit("connected");
  }

  private handle_message(event: MessageEvent): void {
    if (typeof event.data === "string") {
      this.handle_text_message(event.data);
    } else if (event.data instanceof ArrayBuffer) {
      this.handle_binary_message(event.data);
    }
  }

  private handle_text_message(data: string): void {
    try {
      const msg: ServerMessage = JSON.parse(data);

      switch (msg.type) {
        case "stream_start":
          this.handle_stream_start(msg);
          break;
        case "stream_end":
          this.handle_stream_end(msg);
          break;
        case "clip":
          this.pending_clip_meta = msg;
          break;
        case "subscribed":
          this.handle_subscribed(msg as SubscribedMessage);
          break;
        case "error":
          this.handle_server_error(msg as ErrorServerMessage);
          break;
        case "pong":
          break;
        default:
          this.log_warn("Unknown message type:", msg);
      }
    } catch (error) {
      this.log_error("Failed to parse WebSocket message:", error);
    }
  }

  private handle_binary_message(data: ArrayBuffer): void {
    if (this.pending_clip_meta) {
      this.emit("clip_received", this.pending_clip_meta);
      this.pending_clip_meta = null;
    } else if (this.active_session_id) {
      this.append_chunk(data);
    }
  }

  private handle_stream_start(msg: StreamStartMessage): void {
    this.log(`Stream start: session=${msg.session_id}, keywords=${msg.keywords.join(",")}`);
    this.active_session_id = msg.session_id;
    this.start_stream(msg.content_type);
    this.emit("stream_start", msg);
  }

  private handle_stream_end(msg: StreamEndMessage): void {
    this.log(`Stream end: session=${msg.session_id}`);
    this.end_stream();
    this.active_session_id = null;
    this.emit("stream_end", msg);
  }

  private handle_subscribed(msg: SubscribedMessage): void {
    this.log(`Subscribed to: ${msg.keywords.join(",")}`);
    this.emit("subscribed", msg);
  }

  private handle_server_error(msg: ErrorServerMessage): void {
    this.log_error(`Server error: ${msg.message}`);
    const diagnostics = getPlayerFailureDiagnostics(this.url, "v2", msg.message ?? "");
    Sentry.captureException(new Error(`SquawkV2 server error: ${msg.message}`), {
      tags: buildPlayerFailureTags(
        "SquawkV2WebSocket",
        "v2_server_error",
        "v2",
        diagnostics
      ),
      extra: {
        url: this.url,
        serverMessage: msg.message,
        networkDiagnostics: diagnostics.network,
      },
      fingerprint: ["SquawkV2WebSocket", "server-error"],
    });
    this.emit("error", msg.message);
  }

  private handle_close(event: CloseEvent): void {
    this.log(`WebSocket closed: code=${event.code}, reason=${event.reason}`);
    this.ws = null;
    this.active_session_id = null;
    this.pending_clip_meta = null;
    this.set_status("disconnected");
    this.emit("disconnected");
    this.stop_stream();

    if (!this.intentional_disconnect) {
      this.schedule_reconnect();
    }
  }

  private handle_error(): void {
    this.log_error("WebSocket error");
    const diagnostics = getPlayerFailureDiagnostics(this.url, "v2", "ws_error");
    Sentry.captureException(new Error("SquawkV2 WebSocket error"), {
      tags: buildPlayerFailureTags(
        "SquawkV2WebSocket",
        "v2_websocket_error",
        "v2",
        diagnostics
      ),
      extra: {
        url: this.url,
        reconnectAttempts: this.reconnect_attempts,
        networkDiagnostics: diagnostics.network,
      },
      fingerprint: ["SquawkV2WebSocket", "websocket-error"],
    });
    this.emit("error", "WebSocket error");
  }

  // -- Reconnect --

  private schedule_reconnect(): void {
    if (this.reconnect_timeout !== null) return;

    if (this.reconnect_attempts >= MAX_RECONNECT_ATTEMPTS) {
      this.log_error(
        `Max reconnect attempts (${MAX_RECONNECT_ATTEMPTS}) reached — giving up`
      );
      const diagnostics = getPlayerFailureDiagnostics(this.url, "v2", "max_reconnect_attempts");
      Sentry.captureException(
        new Error(`SquawkV2 reconnect exhausted after ${MAX_RECONNECT_ATTEMPTS} attempts`),
        {
          tags: buildPlayerFailureTags(
            "SquawkV2WebSocket",
            "v2_max_reconnect_exhausted",
            "v2",
            diagnostics
          ),
          extra: {
            url: this.url,
            reconnectAttempts: this.reconnect_attempts,
            networkDiagnostics: diagnostics.network,
          },
          fingerprint: ["SquawkV2WebSocket", "max-reconnect-exhausted"],
        }
      );
      this.emit("reconnect_exhausted", this.reconnect_attempts);
      return;
    }

    const delay = Math.min(1000 * Math.pow(2, this.reconnect_attempts), this.max_reconnect_delay);
    this.reconnect_attempts++;

    this.log(`Reconnecting in ${delay}ms (attempt ${this.reconnect_attempts})...`);
    this.emit("reconnecting", this.reconnect_attempts);

    this.reconnect_timeout = setTimeout(() => {
      this.reconnect_timeout = null;
      this.connect();
    }, delay);
  }

  private send(msg: SubscribeCommand | UnsubscribeCommand | PingCommand): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    } else {
      this.log_warn("Cannot send message: WebSocket not connected");
    }
  }

  private set_status(status: ConnectionStatus): void {
    if (this.status !== status) {
      this.status = status;
      this.emit("status_change", status);
    }
  }

  // -- MediaSource streaming --

  private start_stream(content_type: string): void {
    this.log(`Starting stream: content_type=${content_type}`);
    this.stop_stream();

    this.active_audio_element = document.createElement("audio");
    this.active_audio_element.crossOrigin = "anonymous";
    this.active_audio_element.volume = this.current_volume;
    // Attach to the DOM (hidden). A detached media element is aggressively
    // throttled in background tabs — its MediaSource never reaches "sourceopen",
    // so chunks queue forever and playback is silent until the tab is refocused.
    // A live (if invisible) DOM element loads and fires sourceopen while hidden.
    this.active_audio_element.style.display = "none";
    document.body.appendChild(this.active_audio_element);
    this.setup_audio_element_listeners(this.active_audio_element);

    const ms = new MediaSource();
    this.media_source = ms;

    ms.addEventListener("sourceclose", () => {
      this.log("MediaSource sourceclose");
      this.stop_periodic_logging();
    });

    const url = URL.createObjectURL(ms);
    this.current_object_url = url;
    this.active_audio_element.src = url;

    ms.addEventListener("sourceopen", () => {
      this.log(`MediaSource sourceopen: readyState=${ms.readyState}`);
      try {
        // After a previous codec_mismatch in this session, ignore the server's
        // announced content_type and force opus. Sentry NEWSQUAWK-PORTALS-13K
        // shows the actual stream is opus in 100% of mismatch cases.
        const mime_type = this.force_opus_codec
          ? "audio/webm;codecs=opus"
          : content_type.includes("codecs")
            ? content_type
            : "audio/webm;codecs=opus";

        if (this.force_opus_codec) {
          this.log(
            `Forcing audio/webm;codecs=opus after previous codec_mismatch (server announced: ${content_type})`
          );
        }

        this.source_buffer = ms.addSourceBuffer(mime_type);
        this.source_buffer.addEventListener("updateend", () => this.flush_queue());

        // The element plays straight to the default output. We deliberately do
        // NOT route through a Web Audio AudioContext: a suspended context (the
        // default state in background tabs, and until a user gesture on low-MEI
        // origins) silently gates playback. Plain element playback mirrors the
        // reliable MSE/Shoutcast transports.
        this.flush_queue();
        this.emit("stream_started");
      } catch (error) {
        this.log_error("Failed to create SourceBuffer:", error);
        const errMsg = error instanceof Error ? error.message : String(error);
        const diagnostics = getPlayerFailureDiagnostics(this.url, "v2", errMsg);
        Sentry.captureException(
          error instanceof Error ? error : new Error(`SquawkV2 SourceBuffer creation failed: ${errMsg}`),
          {
            tags: buildPlayerFailureTags(
              "SquawkV2WebSocket",
              "v2_sourcebuffer_creation_failed",
              "v2",
              diagnostics
            ),
            extra: {
              url: this.url,
              contentType: content_type,
              networkDiagnostics: diagnostics.network,
            },
            fingerprint: ["SquawkV2WebSocket", "sourcebuffer-creation-failed"],
          }
        );
        this.emit("error", error);
      }
    });

    this.is_streaming = true;
    this.last_known_current_time = 0;
    this.last_current_time_update = Date.now();
    this.gap_recovery_attempts = 0;

    this.active_audio_element.play().catch((err: Error) => {
      const msg = err.message || "";
      if (msg.includes("interrupted") || msg.includes("abort")) {
        return; // Expected during stream/teardown churn
      }
      this.log_warn("Autoplay blocked:", err.message);
      const diagnostics = getPlayerFailureDiagnostics(this.url, "v2", msg);
      Sentry.captureException(err, {
        tags: buildPlayerFailureTags(
          "SquawkV2WebSocket",
          "v2_autoplay_blocked",
          "v2",
          diagnostics
        ),
        extra: {
          url: this.url,
          networkDiagnostics: diagnostics.network,
        },
        fingerprint: ["SquawkV2WebSocket", "autoplay-blocked"],
      });
    });

    this.start_periodic_logging();
  }

  private setup_audio_element_listeners(el: HTMLAudioElement): void {
    el.addEventListener("waiting", () => {
      this.log_warn("Audio waiting for data");
      this.detect_and_recover_gap();
    });

    el.addEventListener("stalled", () => {
      this.log_warn("Audio stalled");
    });

    el.addEventListener("error", () => {
      const error = el.error;
      if (error) {
        // SRC_NOT_SUPPORTED with non-blob src is expected during teardown
        if (error.code === error.MEDIA_ERR_SRC_NOT_SUPPORTED && !el.src.startsWith("blob:")) return;
        const codes = ["", "ABORTED", "NETWORK", "DECODE", "SRC_NOT_SUPPORTED"];
        // error.message carries the real cause (e.g. the demuxer string
        // "CHUNK_DEMUXER_ERROR_APPEND_FAILED: Unexpected element ID …" when the
        // backend sends WebM without a valid init segment). Surface it so the
        // failure can escalate to the player fallback chain (webrtc → v1 audio).
        const message = error.message || "";
        this.log_error(`Audio error: ${codes[error.code] ?? "UNKNOWN"}(${error.code}) ${message}`);
        this.emit("playback_error", { code: error.code, message });
      }
    });

    el.addEventListener("playing", () => {
      this.log(`Audio playing at ${el.currentTime.toFixed(2)}`);
      // Audio is actually rendering — clears the consecutive-failure counter in
      // the hook so a recovered/valid stream doesn't count toward fallback.
      this.emit("playing");
    });
  }

  private start_periodic_logging(): void {
    this.stop_periodic_logging();

    this.periodic_log_interval = setInterval(() => {
      if (!this.is_streaming) return;

      // Track currentTime for stall detection
      if (this.active_audio_element) {
        const ct = this.active_audio_element.currentTime;
        if (ct !== this.last_known_current_time) {
          this.last_known_current_time = ct;
          this.last_current_time_update = Date.now();
        }
      }

      // Check for stall
      if (this.is_playback_stalled()) {
        this.log_warn("Playback stall detected");
        this.detect_and_recover_gap();
      }
    }, 5000);
  }

  private stop_periodic_logging(): void {
    if (this.periodic_log_interval !== null) {
      clearInterval(this.periodic_log_interval);
      this.periodic_log_interval = null;
    }
  }

  private is_playback_stalled(): boolean {
    if (!this.active_audio_element || !this.is_streaming) return false;

    const now = Date.now();
    const stall_threshold_ms = 5000;

    return (
      this.last_current_time_update > 0 &&
      this.active_audio_element.currentTime === this.last_known_current_time &&
      now - this.last_current_time_update > stall_threshold_ms
    );
  }

  private detect_and_recover_gap(): boolean {
    if (!this.is_buffer_ready() || !this.active_audio_element || this.has_media_error()) {
      return false;
    }

    let buffered: TimeRanges;
    try {
      buffered = this.source_buffer!.buffered;
    } catch {
      return false;
    }

    if (buffered.length === 0) return false;

    const current_time = this.active_audio_element.currentTime;
    const buffered_start = buffered.start(0);
    const gap_seconds = buffered_start - current_time;

    if (gap_seconds > 5.0) {
      this.gap_recovery_attempts++;
      if (this.gap_recovery_attempts <= this.max_gap_recovery_attempts) {
        const seek_target = buffered_start + 0.1;
        this.log_warn(`Gap detected (${gap_seconds.toFixed(2)}s), seeking to ${seek_target.toFixed(2)}`);
        this.active_audio_element.currentTime = seek_target;
        this.last_known_current_time = seek_target;
        this.last_current_time_update = Date.now();
        return true;
      }
    } else if (this.gap_recovery_attempts > 0 && gap_seconds < 1.0) {
      this.gap_recovery_attempts = 0;
    }

    return false;
  }

  // -- Buffer management --

  private append_chunk(data: ArrayBuffer): void {
    if (!this.is_streaming || this.has_media_error()) return;

    this.chunk_queue.push(data);

    if (this.chunk_queue.length > 10) {
      this.log_warn(`Chunk queue building up: ${this.chunk_queue.length}`);
    }

    this.flush_queue();
  }

  private end_stream(): void {
    if (!this.is_streaming || this.has_media_error()) return;
    this.flush_queue();
    this.try_end_of_stream();
  }

  private stop_stream(): void {
    this.stop_periodic_logging();

    if (this.media_source) {
      if (this.media_source.readyState === "open") {
        try {
          this.media_source.endOfStream();
        } catch {
          // ignore
        }
      }
      this.media_source = null;
      this.source_buffer = null;
    }

    if (this.current_object_url) {
      URL.revokeObjectURL(this.current_object_url);
      this.current_object_url = null;
    }

    if (this.active_audio_element) {
      this.active_audio_element.pause();
      this.active_audio_element.src = "";
      this.active_audio_element.remove();
      this.active_audio_element = null;
    }

    this.chunk_queue = [];
    this.is_streaming = false;
    this.media_error_count = 0;
    this.gap_recovery_attempts = 0;
    this.last_known_current_time = 0;
    this.last_current_time_update = 0;
    this.emit("stream_ended");
  }

  private flush_queue(): void {
    if (
      !this.is_buffer_ready() ||
      this.source_buffer!.updating ||
      this.chunk_queue.length === 0 ||
      this.has_media_error()
    ) {
      return;
    }

    this.trim_buffer_if_needed();

    if (this.source_buffer!.updating) return;

    const chunk = this.chunk_queue.shift()!;

    try {
      this.source_buffer!.appendBuffer(chunk);

      if (this.media_error_count > 0) {
        this.log(`Buffer recovered from ${this.media_error_count} errors`);
        this.media_error_count = 0;
      }
    } catch (error) {
      this.media_error_count++;

      if (this.is_quota_exceeded_error(error)) {
        this.log_error("QuotaExceededError: buffer full, re-queueing chunk");
        if (this.is_streaming && this.source_buffer && !this.has_media_error()) {
          this.chunk_queue.unshift(chunk);
        }
        this.trim_buffer_aggressive();
      } else {
        // Detect codec_mismatch: Chromium throws QuotaExceededError or a generic
        // Error whose message contains either of the canonical strings.
        // Set the session-scoped override so the NEXT reconnect uses opus
        // regardless of what content_type the server announces.
        const errMsg = error instanceof Error ? error.message : String(error);
        const isCodecMismatch =
          errMsg.includes("CHUNK_DEMUXER_ERROR_APPEND_FAILED") ||
          /Audio stream codec .* doesn'?t match SourceBuffer codecs/i.test(errMsg);
        if (isCodecMismatch && !this.force_opus_codec) {
          this.force_opus_codec = true;
          this.log_error(
            "Codec mismatch detected — forcing audio/webm;codecs=opus on next reconnect"
          );
          Sentry.addBreadcrumb({
            category: "v2-mse",
            level: "warning",
            message: "Codec mismatch — enabling forceOpusCodec for session",
            data: { errMsg },
          });
        }
        this.log_error("appendBuffer failed:", error);
        if (this.is_streaming && this.source_buffer && !this.has_media_error()) {
          this.chunk_queue.unshift(chunk);
        }
      }

      this.emit("error", error);
    }
  }

  private trim_buffer_if_needed(): void {
    if (!this.is_buffer_ready() || this.source_buffer!.updating || this.has_media_error()) return;

    let buffered: TimeRanges;
    try {
      buffered = this.source_buffer!.buffered;
    } catch {
      return;
    }
    if (buffered.length === 0) return;

    const current_time = this.active_audio_element?.currentTime ?? 0;
    const buffered_start = buffered.start(0);
    const buffered_end = buffered.end(buffered.length - 1);
    const buffer_ms = (buffered_end - current_time) * 1000;

    if (buffer_ms > this.max_buffer_ms && current_time > 0.1) {
      const remove_end = Math.max(buffered_start + 0.05, current_time - 0.05);
      if (remove_end > buffered_start) {
        try {
          this.source_buffer!.remove(buffered_start, remove_end);
        } catch (error) {
          this.log_error("Buffer trim failed:", error);
        }
      }
    }
  }

  private trim_buffer_aggressive(): void {
    if (!this.is_buffer_ready() || this.source_buffer!.updating || this.has_media_error()) return;

    let buffered: TimeRanges;
    try {
      buffered = this.source_buffer!.buffered;
    } catch {
      return;
    }
    if (buffered.length === 0) return;

    const current_time = this.active_audio_element?.currentTime ?? 0;
    const buffered_start = buffered.start(0);
    const remove_end = Math.max(buffered_start + 0.1, current_time - 0.1);

    if (remove_end > buffered_start) {
      try {
        this.source_buffer!.remove(buffered_start, remove_end);
      } catch (error) {
        this.log_error("Aggressive buffer trim failed:", error);
      }
    }
  }

  private try_end_of_stream(): void {
    if (this.media_source?.readyState !== "open" || this.has_media_error()) {
      this.cleanup_stream();
      return;
    }

    if (this.source_buffer?.updating) {
      this.source_buffer.addEventListener("updateend", () => this.try_end_of_stream(), {
        once: true,
      });
      return;
    }

    try {
      this.media_source.endOfStream();
    } catch (error) {
      this.log_error("endOfStream failed:", error);
    }

    this.cleanup_stream();
  }

  private cleanup_stream(): void {
    this.stop_periodic_logging();
    this.is_streaming = false;
    this.media_source = null;
    this.source_buffer = null;

    if (this.current_object_url) {
      URL.revokeObjectURL(this.current_object_url);
      this.current_object_url = null;
    }

    // The element itself is left in place to play out any remaining buffered
    // audio; it's torn down (and removed from the DOM) by the next stop_stream()
    // — on the following stream start or on disconnect.
    this.emit("stream_ended");
  }
}
